import React, { useState } from "react";
import axios from "axios";
import { Calendar } from "@/components/ui/calendar";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { toast } from "sonner";
import { CalendarCheck, Phone, Mail, MapPin, Clock, AlertTriangle } from "lucide-react";
import { format } from "date-fns";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const SERVICES = [
  { value: "Personal Care", label: "Personal Care" },
  { value: "Companionship", label: "Companionship & Home Support" },
  { value: "Respite", label: "Respite Care" },
  { value: "Dementia", label: "Alzheimer's & Dementia Care" },
  { value: "Postpartum", label: "Postpartum Recovery" },
  { value: "CDHCI", label: "CDHCI-Funded Care" },
  { value: "Other", label: "Other / Not Sure" },
];

const TIMES = [
  "9:00 AM",
  "10:00 AM",
  "11:00 AM",
  "1:00 PM",
  "2:00 PM",
  "3:00 PM",
  "4:00 PM",
];

export const ContactBooking = () => {
  const [date, setDate] = useState(null);
  const [time, setTime] = useState("");
  const [booking, setBooking] = useState({
    full_name: "",
    email: "",
    phone: "",
    service: "",
    notes: "",
  });
  const [bookingLoading, setBookingLoading] = useState(false);

  const submitBooking = async (e) => {
    e.preventDefault();
    if (!booking.full_name || !booking.email || !booking.phone || !booking.service) {
      toast.error("Please complete all contact fields.");
      return;
    }
    if (!date || !time) {
      toast.error("Please choose a date and time.");
      return;
    }
    setBookingLoading(true);
    try {
      await axios.post(`${API}/booking`, {
        ...booking,
        preferred_date: format(date, "yyyy-MM-dd"),
        preferred_time: time,
      });
      toast.success(`Booked for ${format(date, "PPP")} at ${time}. We'll confirm shortly.`);
      setBooking({ full_name: "", email: "", phone: "", service: "", notes: "" });
      setDate(null);
      setTime("");
    } catch (err) {
      toast.error("Couldn't submit booking. Please call 780-994-6439.");
    } finally {
      setBookingLoading(false);
    }
  };

  return (
    <section
      id="contact"
      data-testid="contact-section"
      className="section-padding bg-white"
    >
      <div className="container-x">
        <div className="max-w-3xl">
          <div className="eyebrow">Request a Free Consultation</div>
          <h2
            data-testid="contact-heading"
            className="font-display text-3xl md:text-5xl font-semibold mt-3 aegis-navy-text"
          >
            Let's build the right care plan together
          </h2>
          <p className="mt-5 text-lg text-gray-600">
            Pick a date and time for a free, no-obligation conversation — we'll
            confirm by phone within 1 business day.
          </p>
        </div>

        <div className="mt-14 max-w-4xl mx-auto">
          <div
            id="booking"
            data-testid="booking-form-card"
            className="rounded-2xl border border-gray-200 p-8 md:p-10 bg-white shadow-sm"
          >
            <h3 className="font-display text-2xl font-semibold aegis-navy-text">
              Book a free consultation
            </h3>
            <p className="mt-2 text-gray-600 text-sm">
              Share your contact details and pick a time that works for your
              family.
            </p>

            <form onSubmit={submitBooking} className="mt-7 space-y-5">
              <div className="grid sm:grid-cols-2 gap-5">
                <Field label="Full Name">
                  <Input
                    data-testid="booking-name"
                    className="h-12 border-gray-300 focus-visible:ring-[#3D7A73]"
                    value={booking.full_name}
                    onChange={(e) =>
                      setBooking({ ...booking, full_name: e.target.value })
                    }
                    placeholder="Jane Doe"
                  />
                </Field>
                <Field label="Phone">
                  <Input
                    data-testid="booking-phone"
                    type="tel"
                    className="h-12 border-gray-300 focus-visible:ring-[#3D7A73]"
                    value={booking.phone}
                    onChange={(e) =>
                      setBooking({ ...booking, phone: e.target.value })
                    }
                    placeholder="(780) 555-0123"
                  />
                </Field>
              </div>
              <Field label="Email">
                <Input
                  data-testid="booking-email"
                  type="email"
                  className="h-12 border-gray-300 focus-visible:ring-[#3D7A73]"
                  value={booking.email}
                  onChange={(e) =>
                    setBooking({ ...booking, email: e.target.value })
                  }
                  placeholder="jane@example.com"
                />
              </Field>

              <Field label="Service Needed">
                <Select
                  value={booking.service}
                  onValueChange={(v) => setBooking({ ...booking, service: v })}
                >
                  <SelectTrigger
                    data-testid="booking-service"
                    className="h-12 border-gray-300"
                  >
                    <SelectValue placeholder="Select a service" />
                  </SelectTrigger>
                  <SelectContent>
                    {SERVICES.map((s) => (
                      <SelectItem key={s.value} value={s.value}>
                        {s.label}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </Field>

              <div className="grid sm:grid-cols-2 gap-5 items-start">
                <div>
                  <label className="block text-sm font-medium aegis-navy-text mb-2">
                    Preferred Date
                  </label>
                  <div className="border border-gray-200 rounded-lg p-2 bg-white" data-testid="booking-calendar">
                    <Calendar
                      mode="single"
                      selected={date}
                      onSelect={setDate}
                      disabled={(d) => d < new Date(new Date().setHours(0,0,0,0))}
                      className="rounded-md"
                    />
                  </div>
                </div>
                <div>
                  <label className="block text-sm font-medium aegis-navy-text mb-2">
                    Preferred Time
                  </label>
                  <div className="grid grid-cols-2 gap-2" data-testid="booking-time-grid">
                    {TIMES.map((t) => (
                      <button
                        type="button"
                        key={t}
                        data-testid={`booking-time-${t.replace(/[: ]/g, "")}`}
                        onClick={() => setTime(t)}
                        className={`h-11 rounded-lg text-sm font-medium border transition-all ${
                          time === t
                            ? "bg-[#3D7A73] text-white border-[#3D7A73]"
                            : "bg-white border-gray-200 text-gray-700 hover:border-[#3D7A73]"
                        }`}
                      >
                        {t}
                      </button>
                    ))}
                  </div>
                  {date && time && (
                    <div className="mt-4 p-3 rounded-lg bg-[#E3EEEC] text-[#2A5C56] text-sm font-medium" data-testid="booking-summary">
                      <CalendarCheck size={16} className="inline mr-2" />
                      {format(date, "PPP")} at {time}
                    </div>
                  )}
                </div>
              </div>

              <div>
                <label className="block text-sm font-medium aegis-navy-text mb-2">
                  Notes (optional)
                </label>
                <div
                  data-testid="booking-notes-warning"
                  className="mb-3 flex gap-2 items-start p-3 rounded-lg bg-[#FFF7EE] border border-[#FBD9B4] text-[13px] text-[#9A3412]"
                >
                  <AlertTriangle size={16} className="flex-shrink-0 mt-0.5" />
                  <span>
                    <strong>Please do not</strong> add any confidential details
                    like driver's licence, passport number, or any health card
                    number. We'll collect sensitive information securely during
                    your consultation.
                  </span>
                </div>
                <Textarea
                  data-testid="booking-notes"
                  className="min-h-[100px] border-gray-300 focus-visible:ring-[#3D7A73]"
                  value={booking.notes}
                  onChange={(e) =>
                    setBooking({ ...booking, notes: e.target.value })
                  }
                  placeholder="Brief context that will help us prepare for our call (no sensitive IDs please)."
                />
              </div>

              <button
                type="submit"
                disabled={bookingLoading}
                data-testid="booking-submit"
                className="btn-primary w-full justify-center disabled:opacity-60 disabled:cursor-not-allowed"
              >
                {bookingLoading ? "Booking..." : "Confirm Booking"}
                {!bookingLoading && <CalendarCheck size={16} />}
              </button>
            </form>
          </div>
        </div>

        <div className="mt-14 grid md:grid-cols-4 gap-6" data-testid="contact-info-strip">
          <InfoCard
            icon={Phone}
            title="24/7 Phone Line"
            value="780-994-6439"
            href="tel:+17809946439"
          />
          <InfoCard
            icon={Mail}
            title="Email"
            value="info@aegishomecare.ca"
            href="mailto:info@aegishomecare.ca"
          />
          <InfoCard
            icon={MapPin}
            title="Coverage"
            value="Edmonton, Calgary & surrounding Alberta"
          />
          <InfoCard
            icon={Clock}
            title="Emergency"
            value="Caregivers on-call, 24/7/365"
          />
        </div>
      </div>
    </section>
  );
};

const Field = ({ label, children }) => (
  <div>
    <label className="block text-sm font-medium aegis-navy-text mb-2">
      {label}
    </label>
    {children}
  </div>
);

const InfoCard = ({ icon: Icon, title, value, href }) => {
  const content = (
    <>
      <div className="w-11 h-11 rounded-lg bg-[#E3EEEC] text-[#2A5C56] flex items-center justify-center mb-4">
        <Icon size={20} />
      </div>
      <div className="text-xs uppercase tracking-widest text-gray-500 font-semibold">
        {title}
      </div>
      <div className="mt-1 font-display font-semibold aegis-navy-text text-lg leading-tight">
        {value}
      </div>
    </>
  );
  return href ? (
    <a
      href={href}
      className="block p-6 rounded-2xl bg-[#F4F7F6] hover:bg-[#E3EEEC] transition-colors"
    >
      {content}
    </a>
  ) : (
    <div className="p-6 rounded-2xl bg-[#F4F7F6]">{content}</div>
  );
};
